import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown, Check } from 'lucide-react';

export interface SelectOption {
  key: string | number;
  text: string;
}

interface SelectProps {
  label?: string;
  options: SelectOption[];
  value?: string | number | null;
  placeholder?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
}

export function Select({
  label,
  options,
  value,
  placeholder = 'Select one',
  onChange,
  required,
  disabled,
}: SelectProps) {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-semibold text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <SelectPrimitive.Root
        value={value != null ? String(value) : undefined}
        onValueChange={onChange}
        disabled={disabled}
        required={required}
      >
        <SelectPrimitive.Trigger className="inline-flex items-center justify-between w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:cursor-not-allowed disabled:opacity-50">
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon>
            <ChevronDown className="h-4 w-4 text-gray-500" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className="z-50 overflow-hidden rounded-md border border-gray-200 bg-white shadow-md animate-in fade-in-0 zoom-in-95"
            position="popper"
            sideOffset={4}
          >
            <SelectPrimitive.Viewport className="p-1 max-h-60">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.key}
                  value={String(option.key)}
                  className="relative flex items-center rounded px-8 py-2 text-sm text-gray-900 cursor-pointer select-none hover:bg-primary/5 focus:bg-primary/10 focus:outline-none data-[highlighted]:bg-primary/10"
                >
                  <SelectPrimitive.ItemIndicator className="absolute left-2 inline-flex items-center">
                    <Check className="h-4 w-4 text-primary" />
                  </SelectPrimitive.ItemIndicator>
                  <SelectPrimitive.ItemText>{option.text}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
}
